// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

/**
 * Runtime configuration loader
 *
 * @see MicroserviceConfigs.js
 *  - for configurations loaded from microservice backend into the browser app
 *
 * This module provides access to runtime configuration injected by the Docker entrypoint.
 * Configuration is set via window.APP_CONFIG by docker-entrypoint.sh
 *
 * In development (when window.APP_CONFIG is not available), it falls back to
 * build-time environment variables from process.env
 *
 * PRECEDENCE:
 * 1. Runtime config (window.APP_CONFIG from docker-entrypoint.sh) - PRODUCTION
 * 2. Build-time env vars (process.env.* from rollup build) - DEVELOPMENT
 *
 * NOTE: When KEYCLOAK_DISABLED=true, Keycloak URL/REALM/CLIENT_ID can be empty/null
 */

/**
 * Helper function to convert a value to a number.
 * Returns the specified default value if the value
 * cannot be converted to a number.
 * @returns {number} Converted number.
 */
function convertToNumberWithDefault(value, defaultValue) {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return defaultValue;
  }
  return number;
}

/**
 * Get runtime configuration
 * @returns {Object} Configuration object
 */
export function getConfig() {
  // RUNTIME CONFIG TAKES PRECEDENCE (production)
  // This is injected at container startup by docker-entrypoint.sh
  if (typeof window !== 'undefined' && window.APP_CONFIG) {
    return window.APP_CONFIG;
  }

  // FALLBACK TO BUILD-TIME ENV VARS (development)
  // These are compiled into the bundle by rollup at build time
  return {
    API_URL: process.env.API_URL || '',
    HUB_BACKEND_URL: process.env.VITE_APP_HUB_BACKEND_URL || '',
    HUB_FSA_URL: process.env.VITE_APP_HUB_FSA_URL || '',
    SHOULD_INCLUDE_CREDENTIALS:
      process.env.VITE_APP_SHOULD_INCLUDE_CREDENTIALS === 'true',
    ADD_TRANSCRIPTION_JOB_ID_TO_ARTIFACTS:
      process.env.ADD_TRANSCRIPTION_JOB_ID_TO_ARTIFACTS === 'true',
    KEYCLOAK_URL: process.env.KEYCLOAK_URL || '',
    KEYCLOAK_REALM: process.env.KEYCLOAK_REALM || '',
    KEYCLOAK_CLIENT_ID: process.env.KEYCLOAK_CLIENT_ID || '',
    KEYCLOAK_DISABLED: process.env.KEYCLOAK_DISABLED === 'true',
    KEYCLOAK_LOGOUT_PATH: process.env.KEYCLOAK_LOGOUT_PATH || '',
    AUTO_LOGIN: process.env.AUTO_LOGIN === 'true',
    IDP_HINT: process.env.IDP_HINT || '',
    CHAT_DOCUMENTS_MAX_FILES: convertToNumberWithDefault(
      process.env.CHAT_DOCUMENTS_MAX_FILES,
      10,
    ),
    CHAT_DOCUMENTS_MAX_TOKENS: convertToNumberWithDefault(
      process.env.CHAT_DOCUMENTS_MAX_TOKENS,
      16000,
    ),
    CHAT_CHAR_LIMIT: convertToNumberWithDefault(
      process.env.CHAT_CHAR_LIMIT,
      4000,
    ),
  };
}

/**
 * Check if Keycloak is enabled
 * @returns {boolean}
 */
export function isKeycloakEnabled() {
  const config = getConfig();
  return !config.KEYCLOAK_DISABLED;
}

/**
 * Check if Autologin is enabled
 * @returns {boolean}
 */
export function isAutoLoginEnabled() {
  const config = getConfig();
  return config.AUTO_LOGIN;
}

/**
 * Get API base URL
 * @returns {string}
 */
export function getApiUrl() {
  return getConfig().API_URL;
}

/**
 * Get Hub backend base URL
 * @returns {string}
 */
export function getHubBackendUrl() {
  return getConfig().HUB_BACKEND_URL;
}

/**
 * Get Hub FSA (federal service adapter) base URL
 * @returns {string}
 */
export function getHubFsaUrl() {
  return getConfig().HUB_FSA_URL;
}

/**
 * Check whether requests should always include credentials
 * @returns {boolean}
 */
export function shouldIncludeCredentials() {
  return getConfig().SHOULD_INCLUDE_CREDENTIALS;
}

/**
 * Get default for rendering job_id in transcription artifacts.
 * @returns {boolean}
 */
export function getAddTranscriptionJobIdToArtifactsDefault() {
  return getConfig().ADD_TRANSCRIPTION_JOB_ID_TO_ARTIFACTS === true;
}

/**
 * Get Keycloak configuration
 * NOTE: Returns empty strings if KEYCLOAK_DISABLED=true
 * @returns {Object} Keycloak config {url, realm, clientId}
 */
export function getKeycloakConfig() {
  const config = getConfig();

  // If Keycloak is disabled, return empty values
  if (config.KEYCLOAK_DISABLED) {
    return {
      url: '',
      realm: '',
      clientId: '',
    };
  }

  return {
    url: config.KEYCLOAK_URL || '',
    realm: config.KEYCLOAK_REALM || '',
    clientId: config.KEYCLOAK_CLIENT_ID || '',
  };
}

/**
 * Get configuration for document store
 * @returns {Object}
 */
export function getDocumentStoreConfig() {
  const config = getConfig();
  return {
    max_documents: config.CHAT_DOCUMENTS_MAX_FILES,
    max_total_tokens: config.CHAT_DOCUMENTS_MAX_TOKENS,
  };
}

/**
 * Get configuration for chat
 * @returns {Object}
 */
export function getChatConfig() {
  const config = getConfig();
  return {
    char_limit: config.CHAT_CHAR_LIMIT,
    context_safe_tokens: config.CHAT_CONTEXT_SAFE_TOKENS,
  };
}

// Export config as default for convenience
export default getConfig();

/**
 * Get WebSocket URL for realtime transcription.
 *
 * Uses the same backend base as regular microservice calls: API_URL when
 * configured, otherwise the current browser origin.
 * @returns {string}
 */
export function getRealtimeTranscriptionWsUrl() {
  const path = '/ws/transcription/realtime';
  const apiBase = String(getApiUrl() || '').replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const httpUrl = new URL(apiBase + path, window.location.href);
    httpUrl.protocol = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    return httpUrl.toString();
  }

  return path;
}
